/*
 * Module for a tenant item
 */
define([
  "marionette",
  "jquery",
  "messaging/messenger",
  "configuration/properties",
  "modules/accountAdmin/tenantAdmin/tenantAdminModal/tenantAdminModalView",
  "modules/accountAdmin/apiKeyAdmin/apiKeyModel",
  "modules/accountAdmin/tenantAdmin/tenantModel",
], function (Marionette, $, Messenger, Properties, TenantAdminModalView, ApiKeyModel, TenantModel) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "tenantItem";
      this.region = options.region;
      this.editMode = options.editMode;
    },
    show: function (tenant) {
      this.tenant = tenant;
      this.allowedRoles = new ApiKeyModel.tenantAllowedRoles();
      this.allowedRoles.fetch();
      this.allowedRoles.onSynced(() => this._copyAllowedRolesToTenant());

      this.tenant.editMode = this.editMode;
      this.tenant.isGlobalAdmin = Messenger.reqres.request("user:is:global:admin");
      this.tenant.showLandlord = Properties.showLandlords;
      this.tenant.authenticationMethodOptions = TenantModel.AUTHENTICATION_METHOD_OPTIONS;

      this.view = new TenantAdminModalView.tenantAdminModalView({
        model: this.tenant,
      });

      this.tenant.backup();

      //handle bad save
      tenant.on(
        "invalid",
        function (_model, error) {
          //Clear errors first
          this.view.clearErrors();
          if ("tenantName" in error) {
            this.view.tenantNameError(error["tenantName"]);
          }
          if ("tenantDesc" in error) {
            this.view.tenantDescriptionError(error["tenantDesc"]);
          }
          if ("landlordId" in error) {
            this.view.landlordError(error["landlordId"]);
          }
          if ("analyticsTagSessionAttribute" in error) {
            this.view.analyticsTagSessionAttributeError(error["analyticsTagSessionAttribute"]);
          }
          if ("emplifiSuiteAccountId" in error) {
            this.view.emplifiSuiteAccountIdError(error["emplifiSuiteAccountId"]);
          }
        },
        this,
      );

      this.view.on(
        "done",
        function () {
          if (Messenger.reqres.request("save:textbox")) {
            //save model
            tenant.set("tenantName", this.view.getTenantName());
            tenant.set("tenantDesc", this.view.getTenantDescription());
            tenant.set("analyticsTagSessionAttribute", this.view.getAnalyticsTagSessionAttribute());
            tenant.set("authenticationMethod", this.view.getAuthenticationMethod());
            tenant.set("loginLockAttempts", this.view.getLoginLockAttempts());
            tenant.set("loginRetryTimeoutMinutes", this.view.getLoginRetryTimeoutMinutes());
            tenant.set("passwordComplexity", this.view.getPasswordComplexity());
            tenant.set("passwordHistoryCount", this.view.getPasswordHistoryCount());
            tenant.set("passwordMaxAge", this.view.getPasswordMaxAge());
            tenant.set("passwordMinAge", this.view.getPasswordMinAge());
            tenant.set("passwordMinLength", this.view.getPasswordMinLength());
            tenant.set("siteMapS3BucketName", this.view.getSiteMapS3BucketName());
            tenant.set("siteMapCloudFront", this.view.getSiteMapCloudFront());
            tenant.set("siteMapBaseUrl", this.view.getSiteMapBaseUrl());
            tenant.set("tableauUser", this.view.getTableauUser());
            tenant.set("emplifiSuiteAccountId", this.view.getEmplifiSuiteAccountId());
            if (Properties.showLandlords) {
              tenant.set("landlordId", this.view.getLandlordId());
            }

            tenant.saveModel({
              success: () => {
                tenant.dirty = false;
                this._updateAllowedRolesFromView();
                this.allowedRoles.saveCollection({
                  success: () => {
                    Messenger.vent.trigger("close:modal");
                    Messenger.vent.trigger("show:account:admin:tenant:profile", { tenant: tenant });
                  },
                  error: (_model, response) => Messenger.showErrorFromResponse(response),
                });
              },
              error: (_model, response) => {
                if (response.status === 400) {
                  if (this.editMode) {
                    Messenger.okDialog(
                      $.t("admin.tenantAdmin.updateError", {
                        message: response.responseJSON.value,
                      }),
                    );
                  } else {
                    Messenger.okDialog(
                      $.t("admin.tenantAdmin.addError", { message: response.responseJSON.value }),
                    );
                  }
                } else {
                  Messenger.showErrorFromResponse(response);
                }
              },
            });
          }
        },
        this,
      );

      this.view.on(
        "cancel",
        function () {
          //if item is new, delete it
          if (tenant.isNew()) {
            tenant.deleteModel();
          } else {
            tenant.restore();
          }
          Messenger.vent.trigger("close:modal");
        },
        this,
      );

      Promise.all([this.tenant.onSyncedPromise(), this.allowedRoles.onSyncedPromise()]).then(() =>
        this.showRegion(this.view),
      );
    },
    _copyAllowedRolesToTenant: function () {
      this.tenant.allowedRoleEngine = false;
      this.tenant.allowedRoleFordApi = false;
      this.tenant.allowedRoleTopicExportApi = false;
      this.tenant.allowedRoleTopicSearchApi = false;
      this.tenant.allowedRoleTopicPublishApi = false;
      this.allowedRoles.forEach((allowedRole) => {
        switch (allowedRole.get("roleId")) {
          case ApiKeyModel.role.ROLE_ENUM.ENGINE:
            this.tenant.allowedRoleEngine = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.FORD_API:
            this.tenant.allowedRoleFordApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_EXPORT_API:
            this.tenant.allowedRoleTopicExportApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_SEARCH_API:
            this.tenant.allowedRoleTopicSearchApi = true;
            break;
          case ApiKeyModel.role.ROLE_ENUM.TOPIC_PUBLISH_API:
            this.tenant.allowedRoleTopicPublishApi = true;
            break;
        }
      });
    },
    _updateAllowedRolesFromView: function () {
      this.allowedRoles.reset([]);
      if (this.view.isAllowedRoleEngine()) {
        this.allowedRoles.addRole(ApiKeyModel.role.ROLE_ENUM.ENGINE);
      }
      if (this.view.isAllowedRoleFordApi()) {
        this.allowedRoles.addRole(ApiKeyModel.role.ROLE_ENUM.FORD_API);
      }
      if (this.view.isAllowedRoleTopicExportApi()) {
        this.allowedRoles.addRole(ApiKeyModel.role.ROLE_ENUM.TOPIC_EXPORT_API);
      }
      if (this.view.isAllowedRoleTopicSearchApi()) {
        this.allowedRoles.addRole(ApiKeyModel.role.ROLE_ENUM.TOPIC_SEARCH_API);
      }
      if (this.view.isAllowedRoleTopicPublishApi()) {
        this.allowedRoles.addRole(ApiKeyModel.role.ROLE_ENUM.TOPIC_PUBLISH_API);
      }
    },
  });
});
